<template>
  <div class="border rounded-lg bg-white">
    <b-row no-gutters>
      <b-col cols="12" lg="4" class="p-3 border-right">
        <select-multi-municipio-section
          v-model="searchRegion"
          :disabled="disabled || loading"
          title="Região"
          subtitle="Selecione para filtrar os estados"
          placeholder="Buscar região..."
        >
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-skeleton
                v-for="index in 4"
                :key="index"
                width="100%"
                height="2.25em"
                class="rounded-lg mb-3 text-gray"
              />
            </template>
            <select-multi-municipio-list-item
              v-for="region in filteredRegions"
              :key="region.regiao"
              :active="selectedRegionValue === region.regiao"
              :disabled="disabled"
              :indeterminate="isIndeterminate('regiao', region.regiao)"
              :icon="selectionActionLabel('regiao', region.regiao)"
              navigate-on-toggle
              show-navigation-icon
              @select="selectRegion(region)"
              @toggle="toggleSelection('regiao', region.regiao)"
            >
              {{ region.nome }}
            </select-multi-municipio-list-item>
          </b-skeleton-wrapper>
        </select-multi-municipio-section>
      </b-col>

      <b-col cols="12" lg="4" class="p-3 border-right">
        <select-multi-municipio-section
          v-model="searchState"
          :disabled="disabled || !selectedRegion"
          title="Estado"
          subtitle="Escolha uma região primeiro"
          placeholder="Buscar estado..."
        >
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-skeleton
                v-for="index in 4"
                :key="index"
                width="100%"
                height="2.25em"
                class="rounded-lg mb-3 text-gray"
              />
            </template>
            <b-list-group-item
              v-if="!selectedRegion"
              class="text-center text-muted"
            >
              Selecione uma região.
            </b-list-group-item>
            <select-multi-municipio-list-item
              v-for="state in filteredStates"
              v-else
              :key="state.sigla"
              :active="selectedStateValue === state.sigla"
              :disabled="disabled"
              :inherited="isInheritedSelection('estado', state.sigla)"
              :indeterminate="isIndeterminate('estado', state.sigla)"
              :icon="selectionActionLabel('estado', state.sigla)"
              navigate-on-toggle
              show-navigation-icon
              @select="selectState(state)"
              @toggle="toggleSelection('estado', state.sigla)"
            >
              {{ state.nome }} / {{ state.sigla }}
            </select-multi-municipio-list-item>
          </b-skeleton-wrapper>
        </select-multi-municipio-section>
      </b-col>

      <b-col cols="12" lg="4" class="p-3">
        <select-multi-municipio-section
          v-model="searchMunicipality"
          :disabled="disabled || !selectedState"
          :list-group="false"
          title="Município"
          subtitle="Adicione municípios avulsos"
          placeholder="Buscar município..."
        >
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-skeleton
                v-for="index in 4"
                :key="index"
                width="100%"
                height="2.25em"
                class="rounded-lg mb-3 text-gray"
              />
            </template>
            <b-list-group v-if="!selectedState">
              <b-list-group-item class="text-center text-muted">
                Selecione um estado.
              </b-list-group-item>
            </b-list-group>
            <b-list-group v-else-if="municipalityCount === 0">
              <b-list-group-item class="text-center text-muted">
                Nenhum município encontrado.
              </b-list-group-item>
            </b-list-group>
            <recycle-scroller
              v-else
              :items="filteredMunicipalities"
              :item-size="49"
              key-field="value"
              class="border rounded overflow-auto"
              style="height: 18rem"
            >
              <template #default="{ item: municipality }">
                <select-multi-municipio-list-item
                  :key="municipality.value"
                  tag="div"
                  :disabled="disabled"
                  :inherited="
                    isInheritedSelection('municipio', municipality.value)
                  "
                  :icon="selectionActionLabel('municipio', municipality.value)"
                  item-class="border-bottom bg-white"
                  item-style="height: 49px"
                  @select="toggleSelection('municipio', municipality.value)"
                  @toggle="toggleSelection('municipio', municipality.value)"
                >
                  {{ municipality.text }}
                </select-multi-municipio-list-item>
              </template>
            </recycle-scroller>
          </b-skeleton-wrapper>
        </select-multi-municipio-section>
      </b-col>
    </b-row>

    <b-alert v-if="isError" show variant="danger" class="m-3">
      Não foi possível carregar os territórios.
      <b-button variant="link" class="p-0" @click="refetch">
        Tentar novamente
      </b-button>
    </b-alert>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from "vue";
import services from "@/http";
import { useFuse } from "@/composables/useFuse";
import { useQuery } from "@/composables/useQuery.js";
import {
  checkAnd,
  safeArray,
  size,
  toArray,
} from "@/helpers/functions/ArrayHelpers";
import SelectMultiMunicipioListItem from "./SelectMultiMunicipioListItem.vue";
import SelectMultiMunicipioSection from "./SelectMultiMunicipioSection.vue";
import { deepCopy } from "@/helpers/functions/ObjectHelpers.js";
import {
  COLLECTION_BY_LEVEL,
  emptyModelValue,
  normalizeModelValue,
  VALUE_KEY_BY_LEVEL,
} from "@/modules/users/helpers/helperSelectMultiMunicipio.js";

export default defineComponent({
  name: "SelectMultiMunicipio",
  components: {
    SelectMultiMunicipioListItem,
    SelectMultiMunicipioSection,
  },
  props: {
    value: {
      type: Object,
      default: emptyModelValue,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["input", "change"],
  setup(props, { emit }) {
    const selectedRegionValue = ref(null);
    const selectedStateValue = ref(null);
    const searchRegion = ref("");
    const searchState = ref("");
    const searchMunicipality = ref("");

    const modelValue = computed({
      get() {
        return normalizeModelValue(props.value);
      },
      set(value) {
        const normalizedValue = normalizeModelValue(value);

        emit("input", normalizedValue);
        emit("change", normalizedValue);
      },
    });

    const {
      data: territories,
      isFetching: loading,
      isError,
      refetch,
    } = useQuery({
      key: ["select-municipios"],
      queryFn: () => services.users.territorios(),
      initialData: () => [],
      select: (response) => safeArray(response.data),
    });

    const mappedDataRegions = computed(() => {
      const regioes = new Map();
      const estados = new Map();
      const municipios = new Map();

      for (const regiao of safeArray(deepCopy(territories.value))) {
        for (const estado of safeArray(regiao.estados)) {
          for (const cidade of safeArray(estado.cidades)) {
            cidade.uf = estado.sigla;
            cidade.regiao = regiao.regiao;

            municipios.set(String(cidade.value), cidade);
          }

          estado.regiao = regiao.regiao;
          delete estado.cidades;
          estados.set(String(estado.sigla), estado);
        }

        delete regiao.estados;
        regioes.set(String(regiao.regiao), regiao);
      }

      return {
        regioes,
        estados,
        municipios,
      };
    });

    const regions = computed(() =>
      toArray(mappedDataRegions.value.regioes.values())
    );

    const selectedRegion = computed(() =>
      mappedDataRegions.value.regioes.get(String(selectedRegionValue.value))
    );

    const selectedState = computed(() =>
      mappedDataRegions.value.estados.get(String(selectedStateValue.value))
    );

    const states = computed(() => {
      const checkStateByRegion = (state) => {
        return String(state.regiao) === String(selectedRegionValue.value);
      };

      const estadosValues = mappedDataRegions.value.estados.values();
      return toArray(estadosValues).filter(checkStateByRegion);
    });

    const municipalities = computed(() => {
      const checkCityByUf = (city) => {
        return city.uf === selectedStateValue.value;
      };

      const municipiosValues = mappedDataRegions.value.municipios.values();
      return toArray(municipiosValues).filter(checkCityByUf);
    });

    const { list: filteredRegions } = useFuse(regions, searchRegion, {
      keys: ["nome", "regiao"],
      threshold: 0.3,
    });

    const { list: filteredStates, clear: clearStateSearch } = useFuse(
      states,
      searchState,
      {
        keys: ["nome", "sigla"],
        threshold: 0.3,
      }
    );

    const {
      list: filteredMunicipalities,
      clear: clearMunicipalitySearch,
      count: municipalityCount,
    } = useFuse(municipalities, searchMunicipality, {
      keys: ["text"],
      threshold: 0.3,
    });

    function selectRegion(region) {
      selectedRegionValue.value = region.regiao;
      selectedStateValue.value = null;
      clearStateSearch();
      clearMunicipalitySearch();
    }

    function selectState(state) {
      selectedStateValue.value = state.sigla;
      clearMunicipalitySearch();
    }

    function getItemValue(level, item) {
      return item?.[VALUE_KEY_BY_LEVEL[level]];
    }

    function hasCollectionValue(level, value, source = modelValue.value) {
      const collection = COLLECTION_BY_LEVEL[level];

      return safeArray(source[collection]).some((selectedValue) => {
        return String(selectedValue) === String(value);
      });
    }

    function getMappedItem(level, value) {
      const collection = COLLECTION_BY_LEVEL[level];

      return mappedDataRegions.value[collection]?.get(String(value));
    }

    function isSelected(level, value) {
      if (isExplicitlySelected(level, value)) return true;

      const item = getMappedItem(level, value);
      if (!item || level === "regiao") return false;

      if (level === "estado") {
        return isExplicitlySelected("regiao", item.regiao);
      }

      return (
        isExplicitlySelected("estado", item.uf) ||
        isExplicitlySelected("regiao", item.regiao)
      );
    }

    function hasSelectedMunicipalityInState(stateValue, source) {
      return safeArray(source.municipios).some((municipalityId) => {
        const municipality = getMappedItem("municipio", municipalityId);

        return String(municipality?.uf) === String(stateValue);
      });
    }

    function isExplicitlySelected(level, value, source = modelValue.value) {
      const hasValue = hasCollectionValue(level, value, source);

      if (!hasValue || level !== "estado") return hasValue;
      return !hasSelectedMunicipalityInState(value, source);
    }

    function isInheritedSelection(level, value) {
      return isSelected(level, value) && !isExplicitlySelected(level, value);
    }

    function isIndeterminate(level, value) {
      if (isExplicitlySelected(level, value)) return false;

      const key = String(value);

      if (level === "estado") {
        return modelValue.value.municipios.some((municipalityId) => {
          return String(getMappedItem("municipio", municipalityId)?.uf) === key;
        });
      }

      if (level === "regiao") {
        const checkStates = (stateId) => {
          return String(getMappedItem("estado", stateId)?.regiao) === key;
        };
        const checkRegions = (municipalityId) => {
          return (
            String(getMappedItem("municipio", municipalityId)?.regiao) === key
          );
        };

        const states = modelValue.value.estados;
        const cities = modelValue.value.municipios;

        const hasSelectedState = states.some(checkStates);
        const hasSelectedMunicipality = cities.some(checkRegions);

        return hasSelectedState || hasSelectedMunicipality;
      }

      return false;
    }

    function selectionActionLabel(level, value) {
      if (isSelected(level, value)) return "square-check";

      return "fa-regular fa-square";
    }

    function removeValue(list, value) {
      return safeArray(list).filter((selectedValue) => {
        return String(selectedValue) !== String(value);
      });
    }

    function filterByMappedValue(list, level, prop, value) {
      return safeArray(list).filter((selectedValue) => {
        return (
          String(getMappedItem(level, selectedValue)?.[prop]) !== String(value)
        );
      });
    }

    function cloneModelValue() {
      return {
        regioes: [...modelValue.value.regioes],
        estados: [...modelValue.value.estados],
        municipios: [...modelValue.value.municipios],
      };
    }

    function addUnique(nextValue, level, items) {
      const collection = COLLECTION_BY_LEVEL[level];

      for (const item of safeArray(items)) {
        const itemId = getItemValue(level, item);
        const value = level === "municipio" ? itemId : String(itemId);

        if (!hasCollectionValue(level, value, nextValue)) {
          nextValue[collection].push(value);
        }
      }
    }

    function compactSelection(nextValue) {
      for (const state of mappedDataRegions.value.estados.values()) {
        const isStateExplicitlySelected = isExplicitlySelected(
          "estado",
          state.sigla,
          nextValue
        );

        if (isStateExplicitlySelected) continue;

        const checkCityByUf = (city) => {
          return String(city.uf) === String(state.sigla);
        };
        const checkSelectedCity = (city) => {
          return hasCollectionValue("municipio", city.value, nextValue);
        };

        const allCities = mappedDataRegions.value.municipios.values();
        const stateCities = toArray(allCities).filter(checkCityByUf);
        const hasCities = size(stateCities) > 0;
        const isEveryCitySelected = stateCities.every(checkSelectedCity);
        const hasAllCities = hasCities && isEveryCitySelected;

        if (!hasAllCities) continue;

        nextValue.municipios = filterByMappedValue(
          nextValue.municipios,
          "municipio",
          "uf",
          state.sigla
        );
        addUnique(nextValue, "estado", [state]);
      }

      for (const region of mappedDataRegions.value.regioes.values()) {
        const isRegionSelected = hasCollectionValue(
          "regiao",
          region.regiao,
          nextValue
        );

        if (isRegionSelected) continue;

        const checkStateByRegion = (state) => {
          return String(state.regiao) === String(region.regiao);
        };
        const checkSelectedState = (state) => {
          return isExplicitlySelected("estado", state.sigla, nextValue);
        };

        const allStates = mappedDataRegions.value.estados.values();
        const regionStates = toArray(allStates).filter(checkStateByRegion);
        const hasStates = size(regionStates) > 0;
        const isEveryStateSelected = regionStates.every(checkSelectedState);
        const hasAllStates = hasStates && isEveryStateSelected;

        if (!hasAllStates) continue;

        nextValue.estados = filterByMappedValue(
          nextValue.estados,
          "estado",
          "regiao",
          region.regiao
        );
        nextValue.municipios = filterByMappedValue(
          nextValue.municipios,
          "municipio",
          "regiao",
          region.regiao
        );
        addUnique(nextValue, "regiao", [region]);
      }

      return nextValue;
    }

    function excludeInheritedSelection(level, value) {
      const item = getMappedItem(level, value);
      if (!item) return;

      const nextValue = cloneModelValue();

      if (level === "estado") {
        nextValue.regioes = removeValue(nextValue.regioes, item.regiao);
        const allStates = mappedDataRegions.value.estados.values();

        const checkSiblingState = (state) => {
          const isSameRegion = String(state.regiao) === String(item.regiao);
          const isDifferentState = String(state.sigla) !== String(value);

          return checkAnd([isSameRegion, isDifferentState]);
        };

        const siblingStates = toArray(allStates).filter(checkSiblingState);

        addUnique(nextValue, "estado", siblingStates);
      }

      if (level === "municipio") {
        const isStateSelected = hasCollectionValue(
          "estado",
          item.uf,
          nextValue
        );
        const isRegionSelected = hasCollectionValue(
          "regiao",
          item.regiao,
          nextValue
        );

        if (isStateSelected) {
          nextValue.estados = removeValue(nextValue.estados, item.uf);
        }

        if (isRegionSelected) {
          nextValue.regioes = removeValue(nextValue.regioes, item.regiao);
          const allStates = mappedDataRegions.value.estados.values();

          const checkSiblingState = (state) => {
            const isSameRegion = String(state.regiao) === String(item.regiao);
            const isDifferentState = String(state.sigla) !== String(item.uf);

            return checkAnd([isSameRegion, isDifferentState]);
          };

          const siblingStates = toArray(allStates).filter(checkSiblingState);

          addUnique(nextValue, "estado", siblingStates);
        }
        const allCities = mappedDataRegions.value.municipios.values();

        const checkSiblingCity = (city) => {
          const isSameState = String(city.uf) === String(item.uf);
          const isDifferentCity = String(city.value) !== String(value);

          return checkAnd([isSameState, isDifferentCity]);
        };

        const siblingCities = toArray(allCities).filter(checkSiblingCity);

        addUnique(nextValue, "municipio", siblingCities);
        addUnique(nextValue, "estado", [getMappedItem("estado", item.uf)]);
      }

      modelValue.value = compactSelection(nextValue);
    }

    function toggleSelection(level, value) {
      if (isExplicitlySelected(level, value)) {
        removeSelection(level, value);
        return;
      }

      if (isInheritedSelection(level, value)) {
        excludeInheritedSelection(level, value);
        return;
      }

      const key = String(value);
      const item = getMappedItem(level, value);
      if (!item) return;

      const nextValue = cloneModelValue();

      if (level === "regiao") {
        nextValue.estados = filterByMappedValue(
          nextValue.estados,
          "estado",
          "regiao",
          key
        );
        nextValue.municipios = filterByMappedValue(
          nextValue.municipios,
          "municipio",
          "regiao",
          key
        );
      }

      if (level === "estado") {
        nextValue.municipios = filterByMappedValue(
          nextValue.municipios,
          "municipio",
          "uf",
          key
        );
      }

      addUnique(nextValue, level, [item]);

      if (level === "municipio") {
        addUnique(nextValue, "estado", [getMappedItem("estado", item.uf)]);
      }

      modelValue.value = compactSelection(nextValue);
    }

    function removeSelection(level, value) {
      const collection = COLLECTION_BY_LEVEL[level];
      const nextValue = cloneModelValue();
      const item = getMappedItem(level, value);
      const isCityLevel = level === "municipio";
      const hasCollectionByUf = hasCollectionValue(
        "estado",
        item.uf,
        nextValue
      );
      const hasSelectedStateByUf = isExplicitlySelected(
        "estado",
        item?.uf,
        nextValue
      );

      const hasImplicitParentState = checkAnd([
        isCityLevel,
        item,
        hasCollectionByUf,
        !hasSelectedStateByUf,
      ]);

      nextValue[collection] = removeValue(nextValue[collection], value);

      const hasSelectedUf = hasSelectedMunicipalityInState(item.uf, nextValue);
      const isRemovableUf = hasImplicitParentState && !hasSelectedUf;

      if (isRemovableUf) {
        nextValue.estados = removeValue(nextValue.estados, item.uf);
      }

      modelValue.value = nextValue;
    }

    function removeAll() {
      modelValue.value = emptyModelValue();
    }

    return {
      modelValue,
      filteredMunicipalities,
      filteredRegions,
      filteredStates,
      isError,
      loading,
      municipalityCount,
      searchMunicipality,
      searchRegion,
      searchState,
      selectedRegion,
      selectedRegionValue,
      selectedState,
      selectedStateValue,

      isInheritedSelection,
      isIndeterminate,
      refetch,
      removeAll,
      removeSelection,
      selectionActionLabel,
      selectRegion,
      selectState,
      toggleSelection,
    };
  },
});
</script>
